import * as L from "leaflet";
import { Pl3xMap } from "../Pl3xMap";
import { ControlBox } from "./ControlBox";
import { createSVGIcon } from "../util/Util";
import Pl3xMapLeafletMap from "../map/Pl3xMapLeafletMap";
import "../svg/marker_point.svg";

/**
 * Toggle that shows / hides the player-placed map markers. Built with the same
 * markup as leaflet's own zoom bar (`leaflet-bar` + an `<a>` button), so it
 * inherits the zoom buttons' look and sits right below them.
 *
 * Markers are visible by default; while hidden the button reads as pressed
 * (aria-pressed) and gets a strike over the pin glyph.
 */
export class MarkerToggleControl extends ControlBox {
    private readonly _container: HTMLDivElement;
    private readonly _button: HTMLAnchorElement;
    private _markersVisible: boolean = true;

    constructor(pl3xmap: Pl3xMap, position: string) {
        super(pl3xmap, position);

        this._container = L.DomUtil.create("div", "leaflet-bar leaflet-control leaflet-control-markers");

        this._button = L.DomUtil.create("a", "leaflet-control-markers__toggle", this._container);
        this._button.href = "#";
        this._button.setAttribute("role", "button");
        this._button.appendChild(createSVGIcon("marker_point"));

        L.DomEvent.disableClickPropagation(this._container);
        L.DomEvent.on(this._button, "click", L.DomEvent.stop);
        L.DomEvent.on(this._button, "click", (): void => this.toggle());

        this.updateButton();
    }

    onAdd(_map: Pl3xMapLeafletMap): HTMLDivElement {
        return this._container;
    }

    /** True while the map markers are shown. */
    get markersVisible(): boolean {
        return this._markersVisible;
    }

    public toggle(): void {
        this.setMarkersVisible(!this._markersVisible);
    }

    public setMarkersVisible(visible: boolean): void {
        if (this._markersVisible === visible) {
            return;
        }

        this._markersVisible = visible;
        // The markers themselves live on the chunk grid control's layer. A fresh
        // control is created on every world switch and reads this flag back from
        // here, so the choice survives changing worlds.
        this._pl3xmap.controlManager.chunkGridControl?.setMarkersVisible(visible);
        this.updateButton();
    }

    private updateButton(): void {
        const label: string = this._markersVisible ? "Приховати маркери" : "Показати маркери";
        this._button.title = label;
        this._button.setAttribute("aria-label", label);
        // Pressed = the "hide markers" toggle is engaged.
        this._button.setAttribute("aria-pressed", String(!this._markersVisible));
    }
}
